// Part of settingsPages, split by screen to keep files short; ../settingsPages.ts merges the parts.
// Company settings (company, sales, expenses, advanced tabs), the shared settings section,
// recurring document templates, profile and security, languages and notification preferences.
export const en = {
  company: {
    settings: {
      terms: {
        dueOnReceipt: 'Due on receipt',
        net_one: 'Net {{count}}',
        net_other: 'Net {{count}}',
      },
      months: {
        january: 'January',
        february: 'February',
        march: 'March',
        april: 'April',
        may: 'May',
        june: 'June',
        july: 'July',
        august: 'August',
        september: 'September',
        october: 'October',
        november: 'November',
        december: 'December',
      },
      emirates: {
        abuDhabi: 'Abu Dhabi',
        dubai: 'Dubai',
        sharjah: 'Sharjah',
        ajman: 'Ajman',
        ummAlQuwain: 'Umm Al Quwain',
        rasAlKhaimah: 'Ras Al Khaimah',
        fujairah: 'Fujairah',
      },
    },
    section: {
      editAria: 'Edit {{title}}',
    },
    page: {
      title: 'Company settings',
      tabs: {
        company: 'Company',
        sales: 'Sales',
        expenses: 'Expenses',
        advanced: 'Advanced',
      },
      tabsAria: 'Settings sections',
      selectClientHint: 'Use the client switcher in the top bar.',
      loadError: 'Could not load settings',
      readOnlyNote: 'Your accountant maintains these settings. Contact them to change anything.',
    },
    companyTab: {
      nameSection: 'Company name',
      companyName: 'Company name',
      legalName: 'Legal name',
      logo: 'Logo',
      logoSoon: 'Logo upload is coming soon.',
      nameRequired: 'Enter the company name.',
      nameHint: 'Shown in the app and on documents.',
      legalNameHint: 'As on the trade licence.',
      nameSaved: 'Company name saved.',
      contactSection: 'Contact info',
      companyEmail: 'Company email',
      companyPhone: 'Company phone',
      website: 'Website',
      invalidEmail: 'Enter a valid email address.',
      contactSaved: 'Contact info saved.',
      addressSection: 'Address',
      companyAddress: 'Company address',
      streetAddress: 'Street address',
      addressLine2: 'Address line 2',
      city: 'City',
      pinCode: 'PIN code',
      state: 'State',
      stateHint: 'Set with the GST registration.',
      country: 'Country',
      india: 'India',
      indiaLine: '{{state}}, India',
      uae: 'United Arab Emirates',
      poBox: 'P.O. Box',
      poBoxLine: 'P.O. Box {{poBox}}',
      emirate: 'Emirate',
      emirateHint: 'Default place of supply.',
      notSet: 'Not set',
      addressSaved: 'Address saved.',
      vatSection: 'VAT registration',
      vatRegistered: 'VAT registered',
      trn: 'TRN',
      pending: 'Pending',
      effectiveDate: 'Effective date of registration',
      filingPeriod: 'VAT filing period',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      taxPack: 'Tax pack',
      taxPackUae: 'UAE VAT (FTA)',
      trnDigits: 'A TRN has 15 digits.',
      trnRequired: 'A VAT-registered company needs its TRN.',
      registeredForVat: 'Registered for VAT',
      trnHint: '15 digits, from the FTA certificate.',
      vatSaved: 'VAT registration saved.',
    },
    salesTab: {
      formSection: 'Sales form content',
      preferredTerms: 'Preferred invoice terms',
      invoicePrefix: 'Invoice number prefix',
      showSku: 'Show SKU column',
      prefixInvalid: 'Up to 10 letters, digits or dashes.',
      prefixHint: 'Invoice numbers are allocated when an invoice is approved.',
      showSkuOnForms: 'Show SKU column on sales forms',
      formSaved: 'Sales form content saved.',
      messagesSection: 'Messages',
      invoiceMessage: 'Default message on invoices',
      statementMessage: 'Default message on statements',
      messagesSaved: 'Messages saved.',
      emailSection: 'Email messages',
      emailSubject: 'Default email subject',
      emailMessage: 'Default email message',
      emailCc: 'Copy (Cc) every sales email to',
      emailCcHint: "Optional, for example the client's accounts mailbox.",
      subjectRequired: 'Enter a subject.',
      messageRequired: 'Enter a message.',
      invalidEmail: 'Enter a valid email address.',
      emailHelp:
        'Used when you send an invoice, credit note, sales receipt or quote. These words are replaced: {document}, {number}, {customer}, {company}, {amount} (the balance due on invoices) and {due date}.',
      emailSaved: 'Email messages saved.',
    },
    expensesTab: {
      section: 'Bills and expenses',
      billTerms: 'Default bill payment terms',
      showItems: 'Show Items table on expense and purchase forms',
      saved: 'Bills and expenses saved.',
    },
    advancedTab: {
      accountingSection: 'Accounting',
      firstMonth: 'First month of financial year',
      closeBooks: 'Close the books',
      closeBooksText: 'Lock months on the Periods and close page: Accounting ›',
      periodsAndClose: 'Periods and close',
      firstMonthChangeable:
        'Re-creates the financial years and their months. Only possible before the first transaction.',
      firstMonthFixed: 'Fixed: the books already have transactions.',
      accountingSaved: 'Accounting saved.',
      coaSection: 'Chart of accounts',
      enableAccountNumbers: 'Enable account numbers',
      chartTemplate: 'Template the chart started from',
      coaSaved: 'Chart of accounts saved.',
      templates: {
        trading: 'Trading (goods)',
        services: 'Services',
        free_zone: 'Free zone company',
        india_trading: 'Trading (goods), India',
        india_services: 'Services, India',
      },
      categoriesSection: 'Categories',
      trackClasses: 'Track classes',
      classModes: {
        off: 'Off',
        per_line: 'One to each row in transaction',
        per_transaction: 'One to entire transaction',
      },
      classesHint: 'Classes are kept at gear › Lists › Classes.',
      trackLocations: 'Track locations',
      locationsOn: 'On, labelled "{{label}}"',
      trackLocationsCheckbox: 'Track locations (one to each transaction)',
      locationLabel: 'Location label',
      locationLabels: {
        location: 'Location',
        business: 'Business',
        department: 'Department',
        division: 'Division',
        property: 'Property',
        store: 'Store',
        territory: 'Territory',
      },
      projects: 'Projects',
      useProjects: 'Use projects',
      categoriesSaved: 'Categories saved.',
      otherSection: 'Other preferences',
      dateFormat: 'Date format',
      numberFormat: 'Number format',
      timeZone: 'Time zone',
      warnInvoice: 'Warn if duplicate invoice number is used',
      warnBill: 'Warn if duplicate bill number is used',
      otherSaved: 'Other preferences saved.',
    },
    templateForm: {
      kinds: {
        invoice: {
          label: 'Invoice',
          title: 'Recurring invoice',
          contact: 'Customer',
          chooseContact: 'Choose a customer',
          contactRequired: 'Choose a customer.',
          reference: 'Reference / PO no.',
          memo: 'Message on invoice',
          autoApprove: 'Approve each invoice automatically',
          autoOn: 'Each invoice is numbered and posted on its date.',
          autoOff: 'Each invoice is created as a draft for you to review and approve.',
        },
        bill: {
          label: 'Bill',
          title: 'Recurring bill',
          contact: 'Vendor',
          chooseContact: 'Choose a vendor',
          contactRequired: 'Choose a vendor.',
          reference: 'Bill no.',
          memo: 'Memo',
          autoApprove: 'Approve each bill automatically',
          autoOn: 'Each bill is numbered and posted on its date.',
          autoOff: 'Each bill is created as a draft for you to review and approve.',
        },
        expense: {
          label: 'Expense',
          title: 'Recurring expense',
          contact: 'Payee',
          chooseContact: 'Choose a payee',
          contactRequired: 'Choose a payee.',
          reference: 'Ref no.',
          memo: 'Memo',
          autoApprove: 'Approve each expense automatically',
          autoOn: 'Each expense is numbered and posted on its date.',
          autoOff: 'Each expense is created as a draft for you to review and approve.',
        },
      },
      taxName: { VAT: 'VAT', GST: 'GST' },
      unsavedConfirm: 'You have unsaved changes. Leave without saving?',
      controlAccount: 'Control accounts post through documents',
      copyName: '{{name}} (copy)',
      saved: 'Recurring template "{{name}}" saved.',
      nameRequired: 'Enter a template name.',
      paymentAccountRequired: 'Choose the payment account.',
      lineRequired: 'Add at least one line.',
      saveTemplate: 'Save template',
      customerEmail: 'Customer email',
      paymentAccount: 'Payment account',
      chooseAccount: 'Choose an account',
      paymentMethod: 'Payment method',
      terms: 'Terms',
      customerDefault: 'Customer default',
      placeOfSupply: 'Place of supply',
      fromCustomer: 'From the customer',
      otherCountries: 'Other countries',
      outsideUae: 'Outside the UAE',
      amountsAre: 'Amounts are',
      exclusive: 'Exclusive of {{tax}}',
      inclusive: 'Inclusive of {{tax}}',
      outOfScope: 'Out of scope of {{tax}}',
      linesAria: 'Lines',
      productService: 'Product/service',
      category: 'Category',
      description: 'Description',
      qty: 'Qty',
      rate: 'Rate',
      amount: 'Amount',
      recoverable: 'Recoverable %',
      recoverableTitle: 'Share of the {{tax}} you can recover',
      lineProduct: 'Line {{n}} product or service',
      lineCategory: 'Line {{n}} category',
      lineDescription: 'Line {{n}} description',
      lineQuantity: 'Line {{n}} quantity',
      lineRate: 'Line {{n}} rate',
      lineTax: 'Line {{n}} {{tax}}',
      lineRecoverable: 'Line {{n}} recoverable percent',
      deleteLine: 'Delete line {{n}}',
      choose: 'Choose',
      addLines: 'Add lines',
      subtotal: 'Subtotal',
      total: 'Total',
    },
    profile: {
      title: 'Profile and security',
      loadError: 'Could not load your profile',
      signInToSee: 'Sign in to see your profile.',
      demoDescription:
        'The demo visitor’s profile is read-only. Create your own account to change yours.',
      description: 'Your name and phone as colleagues and clients see them, and how you sign in.',
      section: 'Profile',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Role',
      clientUser: 'Client user',
      nameRequired: 'Enter your name',
      atMost120: 'At most 120 characters',
      atMost40: 'At most 40 characters',
      emailHint: 'Comes from your sign-in and cannot be changed here.',
      roles: {
        outss_admin: 'Admin',
        outss_accountant: 'Accountant',
        client_owner: 'Client owner',
        client_viewer: 'Client viewer',
      },
      security: 'Sign-in and security',
      signedInAs: 'Signed in as',
      provider: 'Sign-in provider',
      yourProvider: 'your sign-in provider',
      signInOff: 'None (sign-in is off)',
      signedIn: 'Signed in',
      renewsUntil: 'Session renews until',
      password: 'Password and two-step verification',
      managedOn: 'Managed on {{provider}}.',
      managedBy: 'Managed by {{provider}}; ask your administrator to change them.',
      changePassword: 'Change password or two-step verification',
      signOut: 'Sign out',
      access: 'Your access',
      accessHint: 'The clients you can open and what you can do in each.',
      notifications: 'Notifications',
      noClients: 'No clients yet. Ask your accountant to add you.',
      client: 'Client',
      accountants: 'Accountants',
    },
    languages: {
      title: 'Languages',
      arabic: 'Arabic',
      defaultLanguage: 'Default language',
      digits: 'Digits on printed documents',
      arabicCompanyName: 'Arabic company name',
      arabicLegalName: 'Arabic legal name',
      amountInWords: 'Amount in words on invoices',
      languageNames: { en: 'English', ar: 'Arabic' },
      numerals: { latn: 'Western (0-9)', arab: 'Eastern Arabic (٠-٩)' },
      saved: 'Languages saved.',
      arabicCheckbox:
        'Arabic: Arabic names on accounts, products, contacts and VAT codes, and documents in Arabic',
      offNote:
        'Switching Arabic off hides the Arabic fields and prints in English. Arabic names are kept.',
      defaultLanguageHint: 'For new users of this company and for contacts without their own.',
      digitsHint: 'The app, exports and e-invoices always use 0-9.',
      amountInWordsCheckbox: 'Print the amount in words on AED invoices (English and Arabic)',
    },
    arabicEmail: {
      title: 'Arabic email message',
      subject: 'Subject',
      message: 'Message',
      subjectRequired: 'Enter a subject.',
      messageRequired: 'Enter a message.',
      saved: 'Arabic email message saved.',
      help: 'Used when a document goes out in Arabic. Placeholders stay in English: {document}, {number}, {customer}, {company}, {amount}, {due date}.',
    },
    notifications: {
      title: 'Notifications',
      description:
        'Choose which events also reach you by email for this client. Notifications always appear under the bell in the top bar.',
      descriptionNoDelivery:
        'Choose which events also reach you by email for this client. Notifications always appear under the bell in the top bar. This server keeps emails on Sent emails and does not deliver them.',
      loadError: 'Could not load your preferences',
      nothing: 'Nothing to set',
      nothingHint: 'Client viewers see notifications in the app only.',
      event: 'Event',
      email: 'Email',
      emailAria: 'Email for {{event}}',
      emailOn: '{{event}}: email on.',
      emailOff: '{{event}}: email off.',
      events: {
        approval_requested: {
          title: 'Documents awaiting approval',
          hint: 'A draft invoice or bill is waiting for your approval.',
        },
        approval_decided: {
          title: 'Approval decisions',
          hint: 'A document you sent for approval was approved or rejected.',
        },
        upload_received: {
          title: 'New uploads',
          hint: 'The client added a bill or receipt to the document inbox.',
        },
        upload_processed: {
          title: 'Uploads processed',
          hint: 'A bill or receipt you uploaded was recorded, or could not be.',
        },
        vat_return_due: {
          title: 'Tax return due',
          hint: 'A tax return is due within seven days and has not been filed.',
        },
        message_received: {
          title: 'Messages',
          hint: 'Your accountant or your client sent you a message.',
        },
      },
    },
  },
};

export const ar = {
  company: {
    settings: {
      terms: {
        dueOnReceipt: 'مستحق عند الاستلام',
        net_zero: 'صافي {{count}} يوم',
        net_one: 'صافي يوم واحد',
        net_two: 'صافي يومين',
        net_few: 'صافي {{count}} أيام',
        net_many: 'صافي {{count}} يوماً',
        net_other: 'صافي {{count}} يوم',
      },
      months: {
        january: 'يناير',
        february: 'فبراير',
        march: 'مارس',
        april: 'أبريل',
        may: 'مايو',
        june: 'يونيو',
        july: 'يوليو',
        august: 'أغسطس',
        september: 'سبتمبر',
        october: 'أكتوبر',
        november: 'نوفمبر',
        december: 'ديسمبر',
      },
      emirates: {
        abuDhabi: 'أبوظبي',
        dubai: 'دبي',
        sharjah: 'الشارقة',
        ajman: 'عجمان',
        ummAlQuwain: 'أم القيوين',
        rasAlKhaimah: 'رأس الخيمة',
        fujairah: 'الفجيرة',
      },
    },
    section: {
      editAria: 'تعديل {{title}}',
    },
    page: {
      title: 'إعدادات الشركة',
      tabs: {
        company: 'الشركة',
        sales: 'المبيعات',
        expenses: 'المصروفات',
        advanced: 'متقدم',
      },
      tabsAria: 'أقسام الإعدادات',
      selectClientHint: 'استخدم أداة تبديل العميل في الشريط العلوي.',
      loadError: 'تعذّر تحميل الإعدادات',
      readOnlyNote: 'يتولى محاسبك هذه الإعدادات. تواصل معه لتغيير أي شيء.',
    },
    companyTab: {
      nameSection: 'اسم الشركة',
      companyName: 'اسم الشركة',
      legalName: 'الاسم القانوني',
      logo: 'الشعار',
      logoSoon: 'رفع الشعار متاح قريباً.',
      nameRequired: 'أدخل اسم الشركة.',
      nameHint: 'يظهر في التطبيق وعلى المستندات.',
      legalNameHint: 'كما في الرخصة التجارية.',
      nameSaved: 'تم حفظ اسم الشركة.',
      contactSection: 'معلومات الاتصال',
      companyEmail: 'البريد الإلكتروني للشركة',
      companyPhone: 'هاتف الشركة',
      website: 'الموقع الإلكتروني',
      invalidEmail: 'أدخل بريداً إلكترونياً صحيحاً.',
      contactSaved: 'تم حفظ معلومات الاتصال.',
      addressSection: 'العنوان',
      companyAddress: 'عنوان الشركة',
      streetAddress: 'عنوان الشارع',
      addressLine2: 'سطر العنوان 2',
      city: 'المدينة',
      pinCode: 'الرمز البريدي (PIN)',
      state: 'الولاية',
      stateHint: 'تُحدَّد مع التسجيل في ضريبة السلع والخدمات.',
      country: 'الدولة',
      india: 'الهند',
      indiaLine: '{{state}}، الهند',
      uae: 'الإمارات العربية المتحدة',
      poBox: 'صندوق البريد',
      poBoxLine: 'ص.ب. {{poBox}}',
      emirate: 'الإمارة',
      emirateHint: 'مكان التوريد الافتراضي.',
      notSet: 'غير محدد',
      addressSaved: 'تم حفظ العنوان.',
      vatSection: 'التسجيل في ضريبة القيمة المضافة',
      vatRegistered: 'مسجل في ضريبة القيمة المضافة',
      trn: 'رقم التسجيل الضريبي',
      pending: 'قيد الانتظار',
      effectiveDate: 'تاريخ سريان التسجيل',
      filingPeriod: 'فترة تقديم إقرار ضريبة القيمة المضافة',
      monthly: 'شهرية',
      quarterly: 'ربع سنوية',
      taxPack: 'الحزمة الضريبية',
      taxPackUae: 'ضريبة القيمة المضافة في الإمارات (الهيئة الاتحادية للضرائب)',
      trnDigits: 'يتكون رقم التسجيل الضريبي من 15 رقماً.',
      trnRequired: 'الشركة المسجلة في ضريبة القيمة المضافة تحتاج إلى رقم تسجيلها الضريبي.',
      registeredForVat: 'مسجلة في ضريبة القيمة المضافة',
      trnHint: '15 رقماً، من شهادة الهيئة الاتحادية للضرائب.',
      vatSaved: 'تم حفظ التسجيل في ضريبة القيمة المضافة.',
    },
    salesTab: {
      formSection: 'محتوى نماذج المبيعات',
      preferredTerms: 'شروط الدفع المفضلة للفواتير',
      invoicePrefix: 'بادئة رقم الفاتورة',
      showSku: 'إظهار عمود رمز المنتج (SKU)',
      prefixInvalid: 'حتى 10 أحرف أو أرقام أو شرطات.',
      prefixHint: 'تُخصَّص أرقام الفواتير عند اعتماد الفاتورة.',
      showSkuOnForms: 'إظهار عمود رمز المنتج (SKU) في نماذج المبيعات',
      formSaved: 'تم حفظ محتوى نماذج المبيعات.',
      messagesSection: 'الرسائل',
      invoiceMessage: 'الرسالة الافتراضية على الفواتير',
      statementMessage: 'الرسالة الافتراضية على كشوف الحساب',
      messagesSaved: 'تم حفظ الرسائل.',
      emailSection: 'رسائل البريد الإلكتروني',
      emailSubject: 'موضوع البريد الافتراضي',
      emailMessage: 'نص البريد الافتراضي',
      emailCc: 'نسخة (Cc) من كل بريد مبيعات إلى',
      emailCcHint: 'اختياري، مثلاً صندوق بريد الحسابات لدى العميل.',
      subjectRequired: 'أدخل الموضوع.',
      messageRequired: 'أدخل الرسالة.',
      invalidEmail: 'أدخل بريداً إلكترونياً صحيحاً.',
      emailHelp:
        'يُستخدم عند إرسال فاتورة أو إشعار دائن أو إيصال مبيعات أو عرض سعر. تُستبدل هذه الكلمات: {document}، {number}، {customer}، {company}، {amount} (الرصيد المستحق في الفواتير) و{due date}.',
      emailSaved: 'تم حفظ رسائل البريد الإلكتروني.',
    },
    expensesTab: {
      section: 'فواتير الموردين والمصروفات',
      billTerms: 'شروط الدفع الافتراضية لفواتير الموردين',
      showItems: 'إظهار جدول الأصناف في نماذج المصروفات والمشتريات',
      saved: 'تم حفظ إعدادات فواتير الموردين والمصروفات.',
    },
    advancedTab: {
      accountingSection: 'المحاسبة',
      firstMonth: 'أول شهر في السنة المالية',
      closeBooks: 'إقفال الدفاتر',
      closeBooksText: 'أقفل الأشهر من صفحة الفترات والإقفال: المحاسبة ›',
      periodsAndClose: 'الفترات والإقفال',
      firstMonthChangeable: 'يعيد إنشاء السنوات المالية وأشهرها. ممكن فقط قبل أول معاملة.',
      firstMonthFixed: 'ثابت: توجد معاملات في الدفاتر.',
      accountingSaved: 'تم حفظ إعدادات المحاسبة.',
      coaSection: 'دليل الحسابات',
      enableAccountNumbers: 'تفعيل أرقام الحسابات',
      chartTemplate: 'القالب الذي بدأ منه الدليل',
      coaSaved: 'تم حفظ إعدادات دليل الحسابات.',
      templates: {
        trading: 'تجارة (بضائع)',
        services: 'خدمات',
        free_zone: 'شركة منطقة حرة',
        india_trading: 'تجارة (بضائع)، الهند',
        india_services: 'خدمات، الهند',
      },
      categoriesSection: 'التصنيفات',
      trackClasses: 'تتبع الفئات',
      classModes: {
        off: 'غير مفعّل',
        per_line: 'فئة لكل سطر في المعاملة',
        per_transaction: 'فئة للمعاملة بأكملها',
      },
      classesHint: 'تُدار الفئات من الترس › القوائم › الفئات.',
      trackLocations: 'تتبع المواقع',
      locationsOn: 'مفعّل، بتسمية "{{label}}"',
      trackLocationsCheckbox: 'تتبع المواقع (موقع لكل معاملة)',
      locationLabel: 'تسمية الموقع',
      locationLabels: {
        location: 'الموقع',
        business: 'النشاط',
        department: 'القسم',
        division: 'الإدارة',
        property: 'العقار',
        store: 'المتجر',
        territory: 'المنطقة',
      },
      projects: 'المشاريع',
      useProjects: 'استخدام المشاريع',
      categoriesSaved: 'تم حفظ التصنيفات.',
      otherSection: 'تفضيلات أخرى',
      dateFormat: 'تنسيق التاريخ',
      numberFormat: 'تنسيق الأرقام',
      timeZone: 'المنطقة الزمنية',
      warnInvoice: 'التنبيه عند استخدام رقم فاتورة مكرر',
      warnBill: 'التنبيه عند استخدام رقم فاتورة مورد مكرر',
      otherSaved: 'تم حفظ التفضيلات الأخرى.',
    },
    templateForm: {
      kinds: {
        invoice: {
          label: 'فاتورة',
          title: 'فاتورة متكررة',
          contact: 'العميل',
          chooseContact: 'اختر عميلاً',
          contactRequired: 'اختر عميلاً.',
          reference: 'المرجع / رقم أمر الشراء',
          memo: 'رسالة على الفاتورة',
          autoApprove: 'اعتماد كل فاتورة تلقائياً',
          autoOn: 'تُرقَّم كل فاتورة وتُرحَّل في تاريخها.',
          autoOff: 'تُنشأ كل فاتورة كمسودة لتراجعها وتعتمدها.',
        },
        bill: {
          label: 'فاتورة مورد',
          title: 'فاتورة مورد متكررة',
          contact: 'المورد',
          chooseContact: 'اختر مورداً',
          contactRequired: 'اختر مورداً.',
          reference: 'رقم فاتورة المورد',
          memo: 'ملاحظة',
          autoApprove: 'اعتماد كل فاتورة مورد تلقائياً',
          autoOn: 'تُرقَّم كل فاتورة مورد وتُرحَّل في تاريخها.',
          autoOff: 'تُنشأ كل فاتورة مورد كمسودة لتراجعها وتعتمدها.',
        },
        expense: {
          label: 'مصروف',
          title: 'مصروف متكرر',
          contact: 'المستفيد',
          chooseContact: 'اختر مستفيداً',
          contactRequired: 'اختر مستفيداً.',
          reference: 'رقم المرجع',
          memo: 'ملاحظة',
          autoApprove: 'اعتماد كل مصروف تلقائياً',
          autoOn: 'يُرقَّم كل مصروف ويُرحَّل في تاريخه.',
          autoOff: 'يُنشأ كل مصروف كمسودة لتراجعه وتعتمده.',
        },
      },
      taxName: { VAT: 'ضريبة القيمة المضافة', GST: 'ضريبة السلع والخدمات' },
      unsavedConfirm: 'لديك تغييرات غير محفوظة. هل تريد المغادرة دون حفظ؟',
      controlAccount: 'حسابات المراقبة تُرحَّل من خلال المستندات',
      copyName: '{{name}} (نسخة)',
      saved: 'تم حفظ القالب المتكرر "{{name}}".',
      nameRequired: 'أدخل اسم القالب.',
      paymentAccountRequired: 'اختر حساب الدفع.',
      lineRequired: 'أضف سطراً واحداً على الأقل.',
      saveTemplate: 'حفظ القالب',
      customerEmail: 'البريد الإلكتروني للعميل',
      paymentAccount: 'حساب الدفع',
      chooseAccount: 'اختر حساباً',
      paymentMethod: 'طريقة الدفع',
      terms: 'شروط الدفع',
      customerDefault: 'الافتراضي للعميل',
      placeOfSupply: 'مكان التوريد',
      fromCustomer: 'من بيانات العميل',
      otherCountries: 'دول أخرى',
      outsideUae: 'خارج الإمارات',
      amountsAre: 'المبالغ',
      exclusive: 'غير شاملة {{tax}}',
      inclusive: 'شاملة {{tax}}',
      outOfScope: 'خارج نطاق {{tax}}',
      linesAria: 'الأسطر',
      productService: 'المنتج/الخدمة',
      category: 'الفئة',
      description: 'الوصف',
      qty: 'الكمية',
      rate: 'السعر',
      amount: 'المبلغ',
      recoverable: 'نسبة الاسترداد %',
      recoverableTitle: 'حصة {{tax}} التي يمكنك استردادها',
      lineProduct: 'المنتج أو الخدمة في السطر {{n}}',
      lineCategory: 'فئة السطر {{n}}',
      lineDescription: 'وصف السطر {{n}}',
      lineQuantity: 'كمية السطر {{n}}',
      lineRate: 'سعر السطر {{n}}',
      lineTax: '{{tax}} للسطر {{n}}',
      lineRecoverable: 'نسبة الاسترداد للسطر {{n}}',
      deleteLine: 'حذف السطر {{n}}',
      choose: 'اختر',
      addLines: 'إضافة أسطر',
      subtotal: 'المجموع الفرعي',
      total: 'الإجمالي',
    },
    profile: {
      title: 'الملف الشخصي والأمان',
      loadError: 'تعذّر تحميل ملفك الشخصي',
      signInToSee: 'سجّل الدخول لعرض ملفك الشخصي.',
      demoDescription: 'ملف زائر العرض التجريبي للقراءة فقط. أنشئ حسابك الخاص لتعديل ملفك.',
      description: 'اسمك وهاتفك كما يراهما الزملاء والعملاء، وطريقة تسجيل دخولك.',
      section: 'الملف الشخصي',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      role: 'الدور',
      clientUser: 'مستخدم لدى العميل',
      nameRequired: 'أدخل اسمك',
      atMost120: '120 حرفاً كحد أقصى',
      atMost40: '40 حرفاً كحد أقصى',
      emailHint: 'يأتي من حساب تسجيل الدخول ولا يمكن تغييره هنا.',
      roles: {
        outss_admin: 'مسؤول',
        outss_accountant: 'محاسب',
        client_owner: 'مالك لدى العميل',
        client_viewer: 'مشاهد لدى العميل',
      },
      security: 'تسجيل الدخول والأمان',
      signedInAs: 'مسجّل الدخول باسم',
      provider: 'مزوّد تسجيل الدخول',
      yourProvider: 'مزوّد تسجيل الدخول لديك',
      signInOff: 'لا يوجد (تسجيل الدخول معطّل)',
      signedIn: 'وقت تسجيل الدخول',
      renewsUntil: 'تتجدد الجلسة حتى',
      password: 'كلمة المرور والتحقق بخطوتين',
      managedOn: 'تُدار على {{provider}}.',
      managedBy: 'يديرها {{provider}}؛ اطلب من المسؤول لديك تغييرها.',
      changePassword: 'تغيير كلمة المرور أو التحقق بخطوتين',
      signOut: 'تسجيل الخروج',
      access: 'صلاحياتك',
      accessHint: 'العملاء الذين يمكنك فتحهم وما يمكنك فعله لدى كل منهم.',
      notifications: 'الإشعارات',
      noClients: 'لا يوجد عملاء بعد. اطلب من محاسبك إضافتك.',
      client: 'العميل',
      accountants: 'المحاسبون',
    },
    languages: {
      title: 'اللغات',
      arabic: 'العربية',
      defaultLanguage: 'اللغة الافتراضية',
      digits: 'الأرقام في المستندات المطبوعة',
      arabicCompanyName: 'اسم الشركة بالعربية',
      arabicLegalName: 'الاسم القانوني بالعربية',
      amountInWords: 'المبلغ كتابةً على الفواتير',
      languageNames: { en: 'الإنجليزية', ar: 'العربية' },
      numerals: { latn: 'الأرقام الغربية (0-9)', arab: 'الأرقام العربية الشرقية (٠-٩)' },
      saved: 'تم حفظ اللغات.',
      arabicCheckbox:
        'العربية: أسماء عربية للحسابات والمنتجات وجهات الاتصال ورموز ضريبة القيمة المضافة، ومستندات بالعربية',
      offNote: 'إيقاف العربية يخفي الحقول العربية ويطبع بالإنجليزية. تبقى الأسماء العربية محفوظة.',
      defaultLanguageHint: 'للمستخدمين الجدد في هذه الشركة ولجهات الاتصال التي ليس لها لغة خاصة.',
      digitsHint: 'يستخدم التطبيق والتصدير والفواتير الإلكترونية دائماً الأرقام 0-9.',
      amountInWordsCheckbox: 'طباعة المبلغ كتابةً على فواتير الدرهم (بالإنجليزية والعربية)',
    },
    arabicEmail: {
      title: 'رسالة البريد الإلكتروني بالعربية',
      subject: 'الموضوع',
      message: 'الرسالة',
      subjectRequired: 'أدخل الموضوع.',
      messageRequired: 'أدخل الرسالة.',
      saved: 'تم حفظ رسالة البريد الإلكتروني بالعربية.',
      help: 'تُستخدم عند إرسال مستند بالعربية. تبقى الحقول البديلة بالإنجليزية: {document}، {number}، {customer}، {company}، {amount}، {due date}.',
    },
    notifications: {
      title: 'الإشعارات',
      description:
        'اختر الأحداث التي تصلك أيضاً بالبريد الإلكتروني لهذا العميل. تظهر الإشعارات دائماً تحت الجرس في الشريط العلوي.',
      descriptionNoDelivery:
        'اختر الأحداث التي تصلك أيضاً بالبريد الإلكتروني لهذا العميل. تظهر الإشعارات دائماً تحت الجرس في الشريط العلوي. يحتفظ هذا الخادم بالرسائل في صفحة الرسائل المرسلة ولا يسلّمها.',
      loadError: 'تعذّر تحميل تفضيلاتك',
      nothing: 'لا يوجد ما يُضبط',
      nothingHint: 'يرى المشاهدون لدى العميل الإشعارات داخل التطبيق فقط.',
      event: 'الحدث',
      email: 'البريد الإلكتروني',
      emailAria: 'البريد الإلكتروني لـ {{event}}',
      emailOn: '{{event}}: البريد الإلكتروني مفعّل.',
      emailOff: '{{event}}: البريد الإلكتروني غير مفعّل.',
      events: {
        approval_requested: {
          title: 'مستندات بانتظار الاعتماد',
          hint: 'مسودة فاتورة أو فاتورة مورد بانتظار اعتمادك.',
        },
        approval_decided: {
          title: 'قرارات الاعتماد',
          hint: 'اعتُمد مستند أرسلته للاعتماد أو رُفض.',
        },
        upload_received: {
          title: 'ملفات جديدة مرفوعة',
          hint: 'أضاف العميل فاتورة مورد أو إيصالاً إلى صندوق المستندات.',
        },
        upload_processed: {
          title: 'معالجة الملفات المرفوعة',
          hint: 'سُجّلت فاتورة مورد أو إيصال رفعته، أو تعذّر تسجيله.',
        },
        vat_return_due: {
          title: 'إقرار ضريبي مستحق',
          hint: 'إقرار ضريبي مستحق خلال سبعة أيام ولم يُقدَّم بعد.',
        },
        message_received: {
          title: 'الرسائل',
          hint: 'أرسل إليك محاسبك أو عميلك رسالة.',
        },
      },
    },
  },
};
